// First-party analytics — sends events to our backend.
// Falls back to GA4 if configured. Generates a per-session ID.
// Captures UTM params on first visit and includes them in every event.
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "")}/api`;
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

function getSessionId() {
  let sid = sessionStorage.getItem("dgx_session_id");
  if (!sid) {
    sid = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem("dgx_session_id", sid);
  }
  return sid;
}

// Capture UTM params from URL on first call, persist to sessionStorage,
// and return them with every event. Also captures `via` (per-investor link id) if present.
function getAttribution() {
  let attr = null;
  try { attr = JSON.parse(sessionStorage.getItem("dgx_attribution") || "null"); } catch (_) {}
  if (!attr) {
    const url = new URL(window.location.href);
    attr = {};
    UTM_KEYS.forEach((k) => { const v = url.searchParams.get(k); if (v) attr[k] = v.slice(0, 80); });
    const via = url.searchParams.get("via");
    if (via) attr.via = via.slice(0, 80);
    if (Object.keys(attr).length > 0) sessionStorage.setItem("dgx_attribution", JSON.stringify(attr));
  }
  return attr || {};
}

// A/B variant assignment: ?v=a|b in URL wins; else 50/50 hash by session.
export function getVariant(experimentId) {
  const url = new URL(window.location.href);
  const forced = url.searchParams.get("v");
  if (forced === "a" || forced === "b") return forced;
  const key = `dgx_variant_${experimentId}`;
  let v = sessionStorage.getItem(key);
  if (!v) {
    v = Math.random() < 0.5 ? "a" : "b";
    sessionStorage.setItem(key, v);
  }
  return v;
}

export function trackEvent(eventName, metadata = {}) {
  const payload = {
    event: eventName,
    session_id: getSessionId(),
    page: window.location.pathname,
    referrer: document.referrer || null,
    metadata: { ...getAttribution(), ...metadata },
  };
  axios.post(`${API}/analytics/track`, payload).catch(() => {});
  // GA4 forwarding — gtag is injected by initThirdPartyAnalytics() if a
  // measurement ID is configured.
  if (typeof window.gtag === "function") window.gtag("event", eventName, metadata);
  // Mixpanel forwarding — mixpanel.track is auto-loaded by initThirdPartyAnalytics().
  if (window.mixpanel && typeof window.mixpanel.track === "function") {
    try { window.mixpanel.track(eventName, metadata); } catch (_) { /* swallow */ }
  }
}

/**
 * Boot Google Analytics 4 (gtag) and / or Mixpanel based on env vars set in
 * frontend/.env. Safe no-op if either ID is missing — funnel keeps logging to
 * our first-party /api/analytics/track.
 *   REACT_APP_GA4_MEASUREMENT_ID  → "G-XXXXXXXXXX"
 *   REACT_APP_MIXPANEL_TOKEN      → mixpanel project token
 * Call once from App.js on mount.
 */
let _thirdPartyBooted = false;
export function initThirdPartyAnalytics() {
  if (_thirdPartyBooted) return;
  _thirdPartyBooted = true;
  const gaId = process.env.REACT_APP_GA4_MEASUREMENT_ID || "";
  const mpToken = process.env.REACT_APP_MIXPANEL_TOKEN || "";

  if (gaId && /^G-[A-Z0-9]+$/i.test(gaId)) {
    // Inject gtag.js
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", gaId, { send_page_view: true, anonymize_ip: true });
  }

  if (mpToken) {
    // Mixpanel snippet — minimal stub installer; full SDK auto-loaded.
    (function (f, b) {
      if (!b.__SV) {
        let e, g, i, h;
        window.mixpanel = b;
        b._i = [];
        b.init = function (e, f, c) {
          function g(a, d) {
            const b = d.split(".");
            b.length === 2 && (a = a[b[0]], d = b[1]);
            a[d] = function () { a.push([d].concat(Array.prototype.slice.call(arguments, 0))); };
          }
          let a = b;
          typeof c !== "undefined" ? a = b[c] = [] : c = "mixpanel";
          a.people = a.people || [];
          a.toString = function (a) { let d = "mixpanel"; c !== "mixpanel" && (d += "." + c); a || (d += " (stub)"); return d; };
          a.people.toString = function () { return a.toString(1) + ".people (stub)"; };
          i = "disable time_event track track_pageview track_links track_forms register register_once alias unregister identify name_tag set_config reset people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user".split(" ");
          for (h = 0; h < i.length; h++) g(a, i[h]);
          b._i.push([e, f, c]);
        };
        b.__SV = 1.2;
        e = f.createElement("script");
        e.type = "text/javascript";
        e.async = !0;
        e.src = "https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js";
        g = f.getElementsByTagName("script")[0];
        g.parentNode.insertBefore(e, g);
      }
    })(document, window.mixpanel || []);
    if (window.mixpanel && typeof window.mixpanel.init === "function") {
      window.mixpanel.init(mpToken, { track_pageview: true, persistence: "localStorage" });
    }
  }
}

export const analytics = {
  pageView: (page) => trackEvent("page_view", { page }),
  applicationStarted: (role) => trackEvent("application_started", { role }),
  applicationSubmitted: (role, app_id) => trackEvent("application_submitted", { role, app_id }),
  ctaClicked: (label, section) => trackEvent("cta_click", { label, section }),
  sectionViewed: (sectionId) => trackEvent("section_view", { section_id: sectionId }),
  rolePreviewViewed: (role, variant) => trackEvent("role_preview_viewed", { role, variant }),
  demoRequested: (role, app_id) => trackEvent("demo_requested", { role, app_id }),
  demoEntered: (mode, via) => trackEvent("demo_entered", { mode, via }),
  demoExited: () => trackEvent("demo_exited", {}),
  tourStepViewed: (step_id) => trackEvent("tour_step_viewed", { step_id }),
  investorPitchViewed: (slide) => trackEvent("investor_pitch_viewed", { slide }),
  legalPageViewed: (page) => trackEvent("legal_page_viewed", { page }),
};
